<template lang="pug">
.schedule-filter-tags
  .schedule-filter-tags__inner(v-if="dayChipLabel || selectedFilters.length")
    span.schedule-filter-tags__group(v-if="dayChipLabel")
      span.schedule-filter-tags__group-label Day
      button.schedule-filter-tags__chip(
        type="button"
        @click="clearDay")
        | {{ dayChipLabel }}
        span.schedule-filter-tags__chip-x(aria-hidden="true") &times;
    span.schedule-filter-tags__group(
      v-for="group in groupedSelections"
      :key="group.id")
      span.schedule-filter-tags__group-label {{ group.name }}
      button.schedule-filter-tags__chip(
        v-for="filter in group.selected"
        :key="filter.id"
        type="button"
        @click="removeFilter(filter)")
        | {{ filter.value }}
        span.schedule-filter-tags__chip-x(aria-hidden="true") &times;
    button.schedule-filter-tags__clear(
      v-if="selectedFilters.length"
      type="button"
      @click="clearAll") Clear Filters
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDayLabel } from './scheduleResultGrouping.js';

const ALLOWED_GROUPS = ['session_format', 'session_topics', 'session_credit_type'];

export default {
  name: 'ScheduleFiltersTags',

  computed: {
    ...mapGetters(['filters', 'selectedDayValue']),
    dayChipLabel() {
      return this.selectedDayValue ? formatDayLabel(this.selectedDayValue) : '';
    },
    selectedFilters() {
      if (!this.filters) return [];
      return this.filters
        .filter(g => ALLOWED_GROUPS.includes(g.id))
        .flatMap(g => (g.filters || [])
          .filter(f => f.selected)
          .map(f => ({ ...f, groupId: g.id, groupName: g.name })));
    },
    groupedSelections() {
      const byGroup = {};
      this.selectedFilters.forEach(f => {
        if (!byGroup[f.groupId]) {
          byGroup[f.groupId] = { id: f.groupId, name: f.groupName, selected: [] };
        }
        byGroup[f.groupId].selected.push(f);
      });
      return ALLOWED_GROUPS.map(id => byGroup[id]).filter(Boolean);
    },
  },

  methods: {
    clearDay() {
      this.$store.commit('setSelectedDay', null);
      this.$store.dispatch('executeSearch');
    },
    removeFilter(filter) {
      this.$store.commit('setFilterValue', { filter, value: false });
      this.$store.dispatch('executeSearch');
    },
    clearAll() {
      this.selectedFilters.forEach(f => {
        this.$store.commit('setFilterValue', { filter: f, value: false });
      });
      this.$store.dispatch('executeSearch');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

.schedule-filter-tags {
  margin: 16px 0;
  // Reserve one chip row of vertical space even when no filters are applied,
  // so the session list below does not jump when chips toggle on/off.
  min-height: 28px;
}

.schedule-filter-tags__inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}

.schedule-filter-tags__group {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.schedule-filter-tags__group-label {
  font-size: 14px;
  font-weight: 700;
  color: $color-text-dark;
}

.schedule-filter-tags__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 9999px;
  border: 1px solid $color-border;
  background: $color-tag-type-bg;
  color: $color-text-dark;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
}

.schedule-filter-tags__chip-x {
  font-size: 16px;
  line-height: 1;
  color: $color-text-secondary;
}

.schedule-filter-tags__clear {
  margin-left: auto;
  background: white;
  border: 1px solid $color-primary;
  border-radius: 8px;
  padding: 6px 14px;
  color: $color-text-dark;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: $shadow-btn;

  &:hover {
    background: $color-btn-outline-hover-bg;
    border-color: $color-btn-outline-hover-border;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}
</style>
